import { Button, Collapse, Form, Layout, Space, Spin, message } from "antd";
import {
  STORE_REQUISITION_ACKNOWLEDGE,
  STORE_REQUISITION_APPROVE,
  STORE_REQUISITION_CHECK,
  STORE_REQUISITION_LIST,
} from "apiServices/API_ENDPOINTS";
import { getData, postData } from "apiServices/common";
import HandleNotification from "common/Notification";
import AppPageHeader from "common/PageHeader";
import InventoryLayOutCommon from "components/InventoryFabric/LayOut/InventoryLayOutCommon";
import { StoreRequisitionContext } from "context/StoreRequisitionContext/StoreRequisitionContextProvider";
import moment from "moment";
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  STORE_REQUISITION_ACKNOWLEDGE_PERMISSION,
  STORE_REQUISITION_APPROVE_PERMISSION,
  STORE_REQUISITION_CHECK_PERMISSION,
} from "routes/permissions";
import { hasPermission } from "utils/functions";
import StoreRequisitionForm from "../StoreRequisitionForm";

const { Content } = Layout;
const { Panel } = Collapse;

const normalizeStatus = (status) => String(status || "pending").toLowerCase();

const StoreRequisitionView = () => {
  const { id } = useParams();
  const { loadStyleDetailsFromApi, resetStyleDetails, getStyleListByBuyerOrFactory } =
    useContext(StoreRequisitionContext);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [status, setStatus] = useState("pending");
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const fetchDetails = useCallback(async () => {
    setLoading(true);
    const response = await getData(`${STORE_REQUISITION_LIST}/${id}`);

    if (response?.data?.code === 200) {
      const data = response.data.data;
      setStatus(normalizeStatus(data.status));
      form.setFieldsValue({
        buyer_id: data.buyer_id,
        sub_contract_factory_id: data.sub_contract_factory_id,
        entry_date: data.entry_date ? moment(data.entry_date) : null,
        requisition_no: data.requisition_no,
        movement_type: data.movement_type,
        sr_type: data.sr_type,
        status: data.status,
        note: data.note,
      });
      const styles = await getStyleListByBuyerOrFactory({
        buyer_id: data.buyer_id || null,
        sub_contract_factory_id: data.sub_contract_factory_id || null,
      });
      loadStyleDetailsFromApi(data.sr_style_details || [], styles);
    } else {
      message.error("Failed to load store requisition");
    }

    setLoading(false);
  }, [id, form, getStyleListByBuyerOrFactory, loadStyleDetailsFromApi]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  useEffect(() => () => resetStyleDetails(), [resetStyleDetails]);

  const handleAction = async (actionType, endpoint, successMessage) => {
    setActionLoading(actionType);
    const response = await postData(`${endpoint}/${id}`);

    if (response) {
      HandleNotification("success", "bottomRight", successMessage, null);
      await fetchDetails();
    } else {
      HandleNotification("error", "bottomRight", "Something went wrong", null);
    }

    setActionLoading(null);
  };

  const handleApprove = () =>
    handleAction("approve", STORE_REQUISITION_APPROVE, "Store Requisition approved successfully");

  const handleCheck = () =>
    handleAction("check", STORE_REQUISITION_CHECK, "Store Requisition checked successfully");

  const handleAcknowledge = () =>
    handleAction(
      "acknowledge",
      STORE_REQUISITION_ACKNOWLEDGE,
      "Store Requisition acknowledged successfully",
    );

  const normalizedStatus = normalizeStatus(status);
  const showApprove = normalizedStatus === "pending";
  const showCheck = normalizedStatus === "approved";
  const showAcknowledge = normalizedStatus === "checked";

  return (
    <InventoryLayOutCommon>
      <AppPageHeader
        title="Store Requisition - View"
        extra={
          <Space>
            <Button size="small" onClick={() => navigate("/production/store-requisition")}>
              Back
            </Button>
            {showApprove && (
              <Button
                size="small"
                type="primary"
                onClick={handleApprove}
                disabled={!hasPermission([STORE_REQUISITION_APPROVE_PERMISSION])}
                loading={actionLoading === "approve"}
              >
                {hasPermission([STORE_REQUISITION_APPROVE_PERMISSION])
                  ? "Approve"
                  : "No Permission"}
              </Button>
            )}
            {showCheck && (
              <Button
                size="small"
                type="primary"
                onClick={handleCheck}
                disabled={!hasPermission([STORE_REQUISITION_CHECK_PERMISSION])}
                loading={actionLoading === "check"}
              >
                {hasPermission([STORE_REQUISITION_CHECK_PERMISSION])
                  ? "Approved by Store Manager"
                  : "No Permission"}
              </Button>
            )}
            {showAcknowledge && (
              <Button
                size="small"
                type="primary"
                onClick={handleAcknowledge}
                disabled={!hasPermission([STORE_REQUISITION_ACKNOWLEDGE_PERMISSION])}
                loading={actionLoading === "acknowledge"}
              >
                {hasPermission([STORE_REQUISITION_ACKNOWLEDGE_PERMISSION])
                  ? "Acknowledge"
                  : "No Permission"}
              </Button>
            )}
          </Space>
        }
      />
      <Content className="item-details">
        <Spin spinning={loading}>
          <Collapse defaultActiveKey={["1"]} style={{ marginTop: 15 }}>
            <Panel header={<b>Details</b>} key="1">
              <Form layout="vertical" form={form}>
                <StoreRequisitionForm form={form} viewMode />
              </Form>
            </Panel>
          </Collapse>
        </Spin>
      </Content>
    </InventoryLayOutCommon>
  );
};

export default StoreRequisitionView;
